//@ts-nocheck
import { generateClient, GraphQLResult } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import {
	FieldInstance,
	FieldInstanceForResolver,
	FieldTemplate,
	FormGroup,
	FormGroupForResolver,
	FormInstance,
	FormInstanceForResolver,
	FormTemplate,
	FormTemplateForResolver,
	Image,
	Project,
	UserForResolver,
	ProjectForResolver,
} from "db/definitions";
import { bulkSaveImageDocs } from "db/queries/imageQueries";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { batchGetFormGroupsToFormInstance } from "utils/batchGetFormGroups";
import _getFormTemplates from "utils/getFormTemplates";
import getFullFormTemplate from "utils/getFullFormTemplate";
import { recordRepairBreadcrumb, recordSentry } from "utils/recordSentry";
import { confirmFieldInstanceMissing } from "utils/confirmFieldInstanceMissing";
import { planMissingFieldInstances } from "utils/planMissingFieldInstances";
import { consolidateTableInstancesOnOpen } from "utils/consolidateTableInstancesOnOpen";
import { seedLinkedAltIds } from "utils/seedLinkedAltIds";
import { v4 as uuid } from "uuid";
import { useAnalytics } from "./useAnalytics";
import { bulkSaveFieldInstances, createFieldForBatchResolver } from "../db/queries/fieldInstanceQueries";
import { buildFieldTemplate, bulkRetrieveFieldTemplatesByFormTemplate } from "../db/queries/fieldTemplateQueries";
import { getOfflineFormGroupById, saveFormGroup, updateFormGroup as updateFG } from "../db/queries/formGroupQueries";
import { buildFormInstancesByFormGroupId, createFormInstance as createFI, bulkSaveFormInstances } from "../db/queries/formInstanceQueries";
import { buildFormTemplates, getAllFormTemplates, getFormTemplateById } from "../db/queries/formTemplateQueries";
import { createFormGroup, createFormInstance, updateFormGroup as updateFormGroupGql } from "../graphql/mutations";
// @ts-ignore
import { getFormGroup, getFormGroupForExport } from "../graphql/queries.js";
import batchCreateFieldInstances from "../utils/batchCreateFieldInstances";
import batchCreateImages from "../utils/batchCreateImages";
import deleteFormGroups from "../utils/deleteFormGroups";
import getFormGroupForProject from "../utils/getFormGroupForProject";
import getFullFormGroup from "../utils/getFullFormGroup";
import { handleBatchCrud } from "../utils/handleBatchCrud";
import handleProjectUpdate from "../utils/handleProjectUpdate";
import { newOnUpdatedProject } from "graphql/subscriptions";
import useTabs from "./useTabs";
import _getProject from "utils/_getProject";
import { Location } from "./useDrawer";
import batchCreateFormInstances from "utils/batchCreateFormInstances";
import { ProgressMetadata } from "@factorearth/component-library";
import prepopulateCurrentDateAction from "utils/actions/prepopulateCurrentDateAction";
import { GeoLocationService } from "services/location/geoLocationService";
import { brutalDebug } from "utils/brutalDebug";

function useForms(
	project: Project | null,
	online: boolean,
	userDoc: UserForResolver,
	setupDrawer: (location: Location | "") => void,
	setProgress: (newProgress: Partial<ProgressMetadata>) => void,
) {
	const [formTemplates, setFormTemplates] = useState<FormTemplateForResolver[]>([]);
	const [formGroups, setFormGroups] = useState<FormGroup[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	// This is to store analytics obj while the singleton is still updating itself
	const location = useLocation();
	const navigate = useNavigate();

	// Initialize analytics for tracking form and form group events
	const { trackUserBehaviorEvent } = useAnalytics({
		userId: userDoc?.id || "",
		groupId: userDoc?.group || "",
		online,
		autoStart: false,
	});

	// setup the tabs hook here passing in the useField hook and instance refs
	const { tabs, addNewTab, removeTab, handleClearAllTabs, handleRefreshTabs, removeMultipleTabs } = useTabs(
		createNewInstance,
		getSingleFormGroup,
		online,
		userDoc,
		project,
		setupDrawer,
		setProgress,
	);

	// initialize the form groups
	useEffect(() => {
		if (project && !project.offline) {
			setFormGroups(project.formGroups.items || []);
		} else {
			setFormGroups([]);
		}
	}, [online, project]);

	useEffect(() => {
		if (project && !project.offline) {
			const client = generateClient({ authMode: "userPool" });
			client
				.graphql({
					query: newOnUpdatedProject,
					variables: { id: project.id },
					authMode: "userPool",
				})
				// @ts-ignore
				.subscribe({
					next: (listenerData: { data: { newOnUpdatedProject: ProjectForResolver } }) => {
						const newFormGroupId = listenerData.data.newOnUpdatedProject.forceUpdateData?.split("NEW_FORMGROUP_")[1];
						if (newFormGroupId) {
							getNewFormGroup(newFormGroupId);
						}
					},
					error: (error: any) => {
						recordSentry({ error });
					},
				});
		}
	}, [project?.forceUpdateData]);

	// initialize the templates
	useEffect(() => {
		getFormTemplates();
	}, [online, userDoc]);

	// The effect below runs once per user load, but it must see the tabs as they are at that moment, not the ones
	// captured when the effect was created.
	const tabsRef = useRef(tabs);
	tabsRef.current = tabs;

	useEffect(() => {
		// Deep link / refresh into a folder. Only open the tab here: the mounted FormGroup performs the one full load
		// (getSingleFormGroup + verifyFormGroupInstances). Loading here as well ran the repair step twice on the same
		// forms at the same time, which is how duplicate field sets were created.
		const fetchSingleFormGroup = async () => {
			const regex = /tabs\/([^\/]+)/;
			const match = location.pathname.match(regex);
			if (match && userDoc) {
				const id = match[1];
				if (tabsRef.current.some((tab) => tab.props?.name === id)) return;
				const formGroup = online ? await getFormGroupForProject(id) : await getOfflineFormGroupById(id);

				if (formGroup) {
					addNewTab({ id: formGroup.id, name: formGroup.name });
				} else {
					navigate(`/`);
				}
			}
		};
		fetchSingleFormGroup();
	}, [userDoc]);

	/**
	 * This retrieves the form templates from the cloud based on user permissions
	 */
	async function getFormTemplates(): Promise<FormTemplateForResolver[]> {
		if (online) {
			try {
				const templates = await _getFormTemplates();
				setFormTemplates(templates);
				return templates;
			} catch (error) {
				recordSentry({ error, msg: "Error getting templates:" });
			}
		} else {
			// Offline process to return an equivalent result to the online query. It should be identical since this app was built online-first, so the offline queries shoul mimic
			// What the online queries do. If they do, then everything will (in theory) just work!
			const formTemplates = await getAllFormTemplates();
			setFormTemplates(formTemplates);
			return formTemplates;
		}
		return [];
	}

	/**
	 * updates the form template list
	 */
	function addFormTemplateToListInState(newTemplate: FormTemplateForResolver) {
		// Use a functional update to avoid stale state issues
		setFormTemplates((prevTemplates) => {
			const updatedTemplateList = [];
			let isTemplateAdded = false;
			for (let i = 0; i < prevTemplates.length; i++) {
				const formTemplate = prevTemplates[i];
				if (formTemplate.id === newTemplate.id) {
					updatedTemplateList.push(newTemplate);
					isTemplateAdded = true;
				} else {
					updatedTemplateList.push(formTemplate);
				}
			}

			if (!isTemplateAdded) {
				updatedTemplateList.push(newTemplate);
			}

			return updatedTemplateList;
		});
	}

	function removeTemplateFromListInState(deletedTemplateId: string) {
		const newTemplateList = formTemplates.filter((template) => template.id !== deletedTemplateId);
		setFormTemplates(newTemplateList);
	}

	/**
	 * Gets a form group for the project when a new one is made in the cloud
	 * @param id of the formGroup that needs to be retrieved into the project
	 */
	async function getNewFormGroup(id: string): Promise<void> {
		const newFormGroup = await getFormGroupForProject(id);
		if (newFormGroup && project) {
			const reloadedProject = await _getProject(project.id, online);
			const exists = reloadedProject?.formGroups.items.find((formGroup) => formGroup.id === newFormGroup.id);
			if (!exists) {
				const formGroupsCopy = [...project.formGroups.items];
				formGroupsCopy.push(newFormGroup);
				setFormGroups(formGroupsCopy);
			} else {
				setFormGroups(reloadedProject?.formGroups.items || []);
			}
		}
	}

	/**
	 * Gets an export JSON object for each selected form group. This is supported for online only
	 * @param selected ids for the form groups to fetch json.
	 */
	async function getJson(selected: string[]) {
		const jsonPromises = [];
		for (let i = 0; i < selected.length; i++) {
			const id = selected[i];
			const json = getSingleFormGroupJson(id);
			jsonPromises.push(json);
		}

		const json = await Promise.all(jsonPromises);
		return json;
	}

	/**
	 * New method for setting up form groups, to setup the subscription first (6/22/2023)
	 * takes an id
	 * @param formGroupData The formGroup's relevant data. In project.tsx, this is just the name and id, hence the definition
	 */
	function addFormGroup(formGroupData: Pick<FormGroup, "id" | "name"> & Partial<FormGroup>): void {
		if (tabs.length !== 0) {
			for (let i = 0; i < tabs.length; i++) {
				// If this statement is hit, it means the tab is already open (the tab element carries the id as its name prop)
				if (tabs[i].props?.name === formGroupData.id) return;
			}
		}
		addNewTab(formGroupData);
	}

	/**
	 * Get a whole form group with everything by id if it hasnt been retrieved already
	 * @param id the id of the entire form group that is needed
	 */
	async function getSingleFormGroup(id: string): Promise<FormGroup | undefined> {
		if (online) {
			try {
				// db call
				interface QueryResponse {
					getFormGroup: FormGroup;
				}
				const client = generateClient({ authMode: "userPool" });
				const retrieved = (await client.graphql({
					query: getFormGroup,
					variables: { id },
					authMode: "userPool",
				})) as GraphQLResult<QueryResponse>;

				const retrievedFormGroup = retrieved.data.getFormGroup;
				const fullFormGroup = await getFullFormGroup(retrievedFormGroup, online);
				// Need to make sure the form group has all the instances that it needs to.  If someone
				// added in a new field template to a form template in this form group, then those new
				// templates need to have new instances created for them.
				const formGroup = await verifyFormGroupInstances(fullFormGroup, online);
				let retrievedFormTemplates = formTemplates;
				if (!formTemplates || formTemplates.length < 1) {
					retrievedFormTemplates = await getFormTemplates();
				}
				// @ts-ignore
				formGroup.formTemplates = retrievedFormTemplates;
				return formGroup;
			} catch (err) {
				recordSentry({ error: err, msg: "Error retrieving single form group:" });
			}
		} else {
			// We need to get an entire formGroup. We have the id, we just have to build out the tree
			// Step 1: Get the raw formGroup document, all formInstances by formGroupId, and all formTemplates (all the stuff we can get initially)
			// Step 2: Construct the formGroup, and addNewTab

			// Step 1: Get the raw formGroup document, all built formInstances by formGroupId, and all built formTemplates
			const [retrievedFormGroup, builtFormInstances, builtFormTemplates] = await Promise.all([
				getOfflineFormGroupById(id),
				buildFormInstancesByFormGroupId(id),
				buildFormTemplates(),
			]);

			// Quit early if the formGroup isn't found. This can happen if an online user is inside of a formGroup, then
			// goes offline when they don't actually have the formGroup in offline mode
			if (!retrievedFormGroup) return;

			// Step 2: Construct the formGroup, and addNewTab
			const builtFormGroup: FormGroup = {
				...retrievedFormGroup,
				formInstances: {
					items: builtFormInstances,
				},
				// @ts-ignore
				formTemplates: builtFormTemplates,
			};
			const formGroup = await verifyFormGroupInstances(builtFormGroup, online);
			return formGroup;
		}
	}

	/**
	 * Verifies the form group has all the fields necessary for the template provided.  This makes sure if some new things were added
	 * that they will be created.
	 * @param retrievedFormGroup FormGroup that was retrieved and is ready to be loaded in.
	 * @param online boolean for online or offline databases.
	 * @returns Final Form Group
	 */
	async function verifyFormGroupInstances(retrievedFormGroup: FormGroup, online: boolean): FormGroup {
		let formGroup = { ...retrievedFormGroup };
		// Start from the alt instances that LINK / LINK_TO fields and cells already point at. Seeding this map up front
		// means the repair below reuses the alt that holds the value instead of minting a fresh, empty one whenever the
		// first field it happens to visit lacks an altInstanceId.
		let newLinked: Record<string, string> = seedLinkedAltIds(retrievedFormGroup.formInstances?.items ?? []);
		let newInstances: FieldInstanceForResolver[] = [];
		let newImageDocs: Image[] = [];
		let skippedForms = 0;
		/** Alt instances that were referenced but missing from the cloud and are being written again in this pass, by id */
		const recreatedAlts = new Map<string, FieldInstanceForResolver>();
		/** Alt ids already asked about this pass, so each is confirmed at most once */
		const checkedAltIds = new Set<string>();
		if (retrievedFormGroup.formInstances && formGroup.formInstances) {
			for (let i = 0; i < retrievedFormGroup.formInstances.items.length; i++) {
				const formInstance: FormInstance = retrievedFormGroup.formInstances.items[i];
				// Check which templates are missing instances by comparing IDs directly.
				// A naive length comparison is unreliable — orphaned instances (from deleted templates)
				// inflate the count and can mask a genuinely missing template.
				const formTemplate = formInstance.formTemplate;
				if (formTemplate?.fieldTemplates) {
					// Only fill gaps. The decision lives in planMissingFieldInstances so it can be unit tested; it counts an
					// instance as covering its template even when the fieldTemplate document didn't resolve (e.g. templates
					// not yet synced to IndexedDB), and it refuses to create anything when the loaded instances are unusable
					// (none cover a template, or some have no template id at all) — those are the states in which creating
					// produced a complete duplicate set of fields that hid the real ones.
					const plan = planMissingFieldInstances(formInstance.fieldInstances.items, formTemplate.fieldTemplates.items);
					if (plan.skipReason) {
						skippedForms++;
						recordSentry({
							error: new Error(`verifyFormGroupInstances refused to create field instances (${plan.skipReason})`),
							msg: "verifyFormGroupInstances skipped a form with unusable field instances",
							additionalData: {
								formGroupId: formGroup.id,
								formInstanceId: formInstance.id,
								formTemplateId: formTemplate.id,
								online,
								loadedCount: plan.loadedCount,
								coveredCount: plan.coveredCount,
								nullIdCount: plan.nullIdCount,
								templateCount: formTemplate.fieldTemplates.items.length,
							},
						});
					}
					const templatesThatNeedInstances = plan.templatesToCreate;

					if (templatesThatNeedInstances.length > 0) {
						const fieldInstanceObj = await buildFieldInstances(templatesThatNeedInstances, formInstance, formGroup);

						if (fieldInstanceObj.linked) {
							// set the linked on the form group so we can keep track of the alt instance ids for newly
							// created forms within the form group to be setup properly without making a new request
							newLinked = { ...newLinked, ...fieldInstanceObj.linked };
						}

						formGroup.formInstances.items[i].fieldInstances.items = formGroup.formInstances.items[i].fieldInstances.items.concat(
							fieldInstanceObj.fullInstances,
						);
						newInstances = newInstances.concat(fieldInstanceObj.instances);
						newImageDocs = newImageDocs.concat(fieldInstanceObj.imageDocs);
					}

					// A form with two instances for one table template gets folded into the oldest one now (same rule as
					// the sync merge). Gated inside: a form without duplicates, or one whose instances are unusable
					// (plan.skipReason), is left alone.
					if (!plan.skipReason) {
						await consolidateTableInstancesOnOpen(formInstance, formTemplate.fieldTemplates.items, formGroup.id, online);
					}
				}

				// now need to go through the fields and make sure each set of Linked fields has altInstances
				// This is for the situation when a Linked action is added onto an existing template with existing
				// instances in an existing FormGroup, which will cause shenanigans down the road.  So they
				// need to be setup.  The altInsance will need to be created and put in the batch
				// along with the fieldisntance that now needs that instance with its altInstanceId.
				for (let j = 0; j < formInstance.fieldInstances.items.length; j++) {
					const fieldInstance = formInstance.fieldInstances.items[j];
					const template = fieldInstance.fieldTemplate;
					if (!template) {
						console.error("NO TEMPLATE FOUND FOR INSTANCE so it is skipping: ", fieldInstance);
						// going to need to officially handle this situation here with our error handling business.
						continue;
					}

					let preformAction;
					let prepopulate;
					let preformActionData;
					// loop through them action
					for (let i = 0; i < template.action.length; i++) {
						const action = template.action[i];
						if (action === "LINK" || action === "LINK_TO") {
							preformAction = action;
							preformActionData = template.actionData[i];
						}

						if (action === "PREPOPULATE" || action === "PREPOPULATE_PICKLIST") {
							// need to set initial value here if its prepopulated I guess
							// This is also handled in buildFieldInstances, this is just for the new altInstance if they have prepopulate
							// There are probably issues with this way of doing it.
							const preData = template.actionData[i];
							prepopulate = preData;
						}

						if (action === "PREPOPULATE_CURRENT_DATE") {
							prepopulate = prepopulateCurrentDateAction();
						}
					}

					// setting it up to specifically check for Link here in case we need to do this for
					// other actions in the future.
					if (preformAction === "LINK" || preformAction === "LINK_TO") {
						const actionId = preformActionData;
						let altFieldObj;
						if (actionId) {
							// The field points at an alt instance that does not exist (its batch was dropped when it was created), so
							// every edit to it fails. Recreate the alt under the same id: the other forms pointing at it heal too.
							// Only online, and only after the cloud confirms the id is really absent, since the batch write would
							// replace an alt that merely could not be read.
							if (fieldInstance.altInstanceId && !fieldInstance.altInstance) {
								const altId = fieldInstance.altInstanceId;
								if (!recreatedAlts.has(altId) && online && !checkedAltIds.has(altId)) {
									checkedAltIds.add(altId);
									if (await confirmFieldInstanceMissing(altId)) {
										altFieldObj = { ...handleLinkedSetup(fieldInstance), id: altId };
										recreatedAlts.set(altId, altFieldObj);
										newInstances.push(altFieldObj);
									}
								}
								const recreated = recreatedAlts.get(altId);
								if (recreated) {
									fieldInstance.altInstance = recreated;
									newLinked[actionId] = altId;
								}
							}
							if (newLinked && !newLinked[actionId]) {
								// If the id doesnt already exist in the linked map setup the alt doc

								if (!fieldInstance.altInstanceId) {
									altFieldObj = handleLinkedSetup(fieldInstance);
									fieldInstance.altInstanceId = altFieldObj.id;
									fieldInstance.altInstance = altFieldObj;
									newLinked[actionId] = altFieldObj.id;
									const timestamp = new Date().toISOString();
									// Built through the resolver shape so nothing display-only (tableFields, fieldTemplate, altInstance) reaches the batch
									const fieldInstanceCopy = createFieldForBatchResolver({
										id: fieldInstance.id,
										altInstanceId: fieldInstance.altInstanceId,
										formGroup: fieldInstance.formGroup,
										formInstance: fieldInstance.formInstance,
										fieldTemplateId: fieldInstance.fieldTemplate?.id || null,
										value: prepopulate || "",
										group: formInstance.group,
										createdAt: timestamp,
										updatedAt: timestamp,
									});
									formGroup.formInstances.items[i].fieldInstances.items[j] = fieldInstance;
									newInstances.push(altFieldObj);
									newInstances.push(fieldInstanceCopy);
								} else {
									newLinked[actionId] = fieldInstance.altInstanceId;
								}
							} else if (newLinked && newLinked[actionId]) {
								if (!fieldInstance.altInstanceId) {
									// the linked already exists so just set this alt id to that
									fieldInstance.altInstanceId = newLinked[actionId];
									const timestamp = new Date().toISOString();
									// Built through the resolver shape so nothing display-only (tableFields, fieldTemplate, altInstance) reaches the batch
									const fieldInstanceCopy = createFieldForBatchResolver({
										id: fieldInstance.id,
										altInstanceId: fieldInstance.altInstanceId,
										formGroup: fieldInstance.formGroup,
										formInstance: fieldInstance.formInstance,
										fieldTemplateId: fieldInstance.fieldTemplate?.id || null,
										value: prepopulate || "",
										group: formInstance.group,
										createdAt: timestamp,
										updatedAt: timestamp,
									});
									formGroup.formInstances.items[i].fieldInstances.items[j] = fieldInstance;

									newInstances.push(fieldInstanceCopy);
								}
							}
						}
					}
					// Repair table/imageroll cells missing altInstanceId for LINK action templates
					if ((template.inputType === "imageroll" || template.inputType === "table") && fieldInstance.tableFields?.items?.length) {
						for (const cell of fieldInstance.tableFields.items) {
							if (!cell.fieldTemplate?.action?.length) continue;
							let cellPreformAction: string | undefined;
							let cellPreformActionData: string | undefined;
							for (let ci = 0; ci < cell.fieldTemplate.action.length; ci++) {
								const cellAction = cell.fieldTemplate.action[ci];
								if (cellAction === "LINK" || cellAction === "LINK_TO") {
									cellPreformAction = cellAction;
									cellPreformActionData = cell.fieldTemplate.actionData[ci];
								}
							}
							if ((cellPreformAction === "LINK" || cellPreformAction === "LINK_TO") && cellPreformActionData) {
								const cellActionId = cellPreformActionData;
								const timestamp = new Date().toISOString();
								if (!cell.altInstanceId) {
									if (newLinked && newLinked[cellActionId]) {
										cell.altInstanceId = newLinked[cellActionId];
										const cellCopy: FieldInstanceForResolver = {
											id: cell.id,
											altInstanceId: cell.altInstanceId,
											formGroup: fieldInstance.formGroup,
											tableField: (cell as FieldInstance).tableField,
											fieldTemplateId: cell.fieldTemplate?.id || null,
											value: cell.value || "",
											authors: cell.authors || [],
											lastAuthor: cell.lastAuthor || "",
											group: formInstance.group,
											createdAt: cell.createdAt,
											updatedAt: timestamp,
											notes: cell.notes || "",
											editing: cell.editing || "",
											fieldsOrder: cell.fieldsOrder || null,
										};
										// This is a full put of the cell, so anything left out is erased. Keep the image link.
										if ((cell as FieldInstance).imageId) cellCopy.imageId = (cell as FieldInstance).imageId;
										newInstances.push(cellCopy);
									} else if (newLinked) {
										const altFieldObj = handleLinkedSetup(cell as FieldInstanceForResolver);
										cell.altInstanceId = altFieldObj.id;
										(cell as FieldInstance).altInstance = altFieldObj;
										newLinked[cellActionId] = altFieldObj.id;
										const cellCopy: FieldInstanceForResolver = {
											id: cell.id,
											altInstanceId: cell.altInstanceId,
											formGroup: fieldInstance.formGroup,
											tableField: (cell as FieldInstance).tableField,
											fieldTemplateId: cell.fieldTemplate?.id || null,
											value: cell.value || "",
											authors: cell.authors || [],
											lastAuthor: cell.lastAuthor || "",
											group: formInstance.group,
											createdAt: cell.createdAt,
											updatedAt: timestamp,
											notes: cell.notes || "",
											editing: cell.editing || "",
											fieldsOrder: cell.fieldsOrder || null,
										};
										// This is a full put of the cell, so anything left out is erased. Keep the image link.
										if ((cell as FieldInstance).imageId) cellCopy.imageId = (cell as FieldInstance).imageId;
										newInstances.push(altFieldObj);
										newInstances.push(cellCopy);
									}
								} else if (newLinked && !newLinked[cellActionId]) {
									newLinked[cellActionId] = cell.altInstanceId;
								}
							}
						}
					}
				}
			}
		}

		const fieldInstances = newInstances;

		if (fieldInstances.length > 0 || newImageDocs.length > 0 || skippedForms > 0) {
			// Every document written here is a repair the user did not ask for; leave a trail so the next incident can be
			// reconstructed from Sentry instead of from S3 backups.
			recordRepairBreadcrumb("verifyFormGroupInstances", {
				formGroupId: formGroup.id,
				online,
				fieldInstancesWritten: fieldInstances.length,
				altInstancesWritten: fieldInstances.filter((instance) => !instance.fieldTemplateId).length,
				altInstancesRecreated: recreatedAlts.size,
				altInstanceIdsRecreated: [...recreatedAlts.keys()],
				cellsWritten: fieldInstances.filter((instance) => !!instance.tableField).length,
				imageDocsWritten: newImageDocs.length,
				formsSkipped: skippedForms,
			});
		}

		if (online) {
			await handleBatchCrud(fieldInstances, batchCreateFieldInstances, 25);
			await handleBatchCrud(newImageDocs, batchCreateImages, 25);
		} else {
			await bulkSaveFieldInstances(fieldInstances);
			await bulkSaveImageDocs(newImageDocs);
		}

		// @ts-ignore
		formGroup.linked = newLinked;
		return formGroup;
	}

	/**
	 * Get a whole form group with everything by id if it hasnt been retrieved already
	 * @param id the id of the entire form group that is needed
	 */
	async function getSingleFormGroupJson(id: string) {
		try {
			interface QueryResponse {
				getFormGroup: FormGroup;
			}
			const client = generateClient({ authMode: "userPool" });
			const retrieved = (await client.graphql({
				query: getFormGroupForExport,
				variables: { id },
				authMode: "userPool",
			})) as GraphQLResult<QueryResponse>;

			const retrievedFormGroup = retrieved.data.getFormGroup;
			// Exporting is a read. Missing instances are repaired when the folder is opened (verifyFormGroupInstances in
			// getSingleFormGroup); doing it here too meant a download could write to the database.
			const formGroup = await getFullFormGroup(retrievedFormGroup, online);
			// For now need to add on the formtempaltes, so the tab can setup the form group
			// properly
			// @ts-ignore
			formGroup.formTemplates = formTemplates;
			return formGroup;
		} catch (err) {
			recordSentry({ error: err, msg: "Error retrieving single form group:" });
		}
	}

	async function createNewFormGroup(newFormGroup: { templates: string[]; name: string; type: "default" | "photolog" }): Promise<FormGroup | undefined> {
		if (!project) return;
		try {
			if (online) {
				const userSession = await fetchAuthSession();
				const groups = userSession.tokens?.accessToken.payload["cognito:groups"] as string[] | undefined;
				if (groups) {
					const group = groups.find((grp: string) => !grp.includes("_"));
					if (group) {
						const formGroupId = uuid();
						const timeStamp = new Date().toISOString();

						const formGroupForResolver: FormGroupForResolver = {
							id: formGroupId,
							name: newFormGroup.name,
							formOrder: [],
							type: newFormGroup.type,
							projectId: project.id,
							group,
							createdAt: timeStamp,
							updatedAt: timeStamp,
							lastUpdatedBy: userDoc.username,
							syncedAt: "",
						};

						const formGroup: FormGroup = { ...formGroupForResolver };
						const client = generateClient({ authMode: "userPool" });

						let allFieldInstances: FieldInstanceForResolver[] = [];
						let allImageDocs: Image[] = [];
						let allFormInstances: FormInstanceForResolver[] = [];
						let allFullForms: FormInstance[] = [];

						for (let i = 0; i < newFormGroup.templates.length; i++) {
							const currentFormTemplate = newFormGroup.templates[i];
							const formTemplate = await getFullFormTemplate(currentFormTemplate, online);

							if (formTemplate && formTemplate.fieldTemplates) {
								const formInstanceId = uuid();
								formGroupForResolver.formOrder.push(formInstanceId);
								// @ts-ignore
								formGroup.formTemplates = formTemplates;
								const formInstance: FormInstanceForResolver = {
									id: formInstanceId,
									formTemplateId: formTemplate.id,
									group,
									formGroup: formGroupId,
									createdAt: timeStamp,
									updatedAt: timeStamp,
								};

								allFormInstances.push(formInstance);
								const fieldInstanceObj = await buildFieldInstances(formTemplate.fieldTemplates.items, formInstance, formGroup);

								if (fieldInstanceObj.linked) {
									// @ts-ignore
									formGroup.linked = fieldInstanceObj.linked;
								}

								const fieldInstances = fieldInstanceObj.instances;
								allFieldInstances = allFieldInstances.concat(fieldInstances);
								allImageDocs = allImageDocs.concat(fieldInstanceObj.imageDocs);

								const fullForm: FormInstance = {
									...formInstance,
									formTemplate: formTemplate,
									fieldInstances: { items: fieldInstanceObj.fullInstances },
								};

								allFullForms.push(fullForm);
							}
						}

						formGroup.formInstances = {
							items: allFullForms,
						};

						await client.graphql({
							query: createFormGroup,
							variables: { input: formGroupForResolver },
							authMode: "userPool",
						});
						await Promise.all([
							handleBatchCrud(allFormInstances, batchCreateFormInstances, 25),
							handleBatchCrud(allFieldInstances, batchCreateFieldInstances, 25),
							handleBatchCrud(allImageDocs, batchCreateImages, 25),
						]);
						const forceUpdateDataString = `NEW_FORMGROUP_${formGroup.id}`;
						const updatedProject = { ...project, forceUpdateData: forceUpdateDataString };
						await handleProjectUpdate(updatedProject); // NEEDS TO BE AWAITED FOR THE SUBS

						const newFormGroups = [...formGroups];
						newFormGroups.push(formGroup);
						setFormGroups(newFormGroups);

						// Track form group creation
						try {
							await trackUserBehaviorEvent("form_group_create", {
								formGroupId: formGroup.id,
								formGroupName: formGroup.name,
								formGroupType: formGroup.type,
								projectId: project.id,
								userId: userDoc.id,
								groupId: userDoc.group,
							});

							// Track creation of each form in the group
							for (const form of allFullForms) {
								await trackUserBehaviorEvent("form_create", {
									formId: form.id,
									formName: form.formTemplate?.templateName ?? (form.formTemplate?.template || "Unknown template"),
									formGroupId: formGroup.id,
									templateUsed: true,
									formTemplateId: form.formTemplateId || "",
									userId: userDoc.id,
									groupId: userDoc.group,
								});
							}
						} catch (error) {
							console.error("Failed to track form group/form creation events:", error);
						}

						return formGroup;
					}
				}
			} else {
				// Offline mode - similar structure to online mode
				const group = userDoc.group;
				const formGroupId = uuid();
				const timeStamp = new Date().toISOString();

				const formGroupForResolver: FormGroupForResolver = {
					id: formGroupId,
					projectId: project.id,
					name: newFormGroup.name,
					formOrder: [],
					type: newFormGroup.type,
					group,
					users: [],
					createdAt: timeStamp,
					updatedAt: timeStamp,
					syncedAt: "",
					lastUpdatedBy: userDoc.username,
				};

				const formGroup: FormGroup = { ...formGroupForResolver };
				// @ts-ignore
				formGroup.formTemplates = formTemplates;

				let allFieldInstances: FieldInstanceForResolver[] = [];
				let allImageDocs: Image[] = [];
				let allFormInstances: FormInstanceForResolver[] = [];
				let allFullForms: FormInstance[] = [];

				for (let i = 0; i < newFormGroup.templates.length; i++) {
					const currentFormTemplate = newFormGroup.templates[i];
					const formTemplate = await getFormTemplateById(currentFormTemplate);

					if (formTemplate) {
						const formInstanceId = uuid();
						formGroupForResolver.formOrder.push(formInstanceId);

						const formInstance: FormInstanceForResolver = {
							id: formInstanceId,
							formTemplateId: formTemplate.id,
							group,
							formGroup: formGroupId,
							createdAt: timeStamp,
							updatedAt: timeStamp,
						};

						allFormInstances.push(formInstance);

						// Get the fieldTemplates that the form needs
						const retrievedFieldTemplates = await bulkRetrieveFieldTemplatesByFormTemplate(formTemplate.id);

						// Create array of promises for the built fields
						const fieldTemplatePromises = [];
						for (const fieldTemplate of retrievedFieldTemplates) {
							fieldTemplatePromises.push(buildFieldTemplate(fieldTemplate));
						}

						// Await promises and build the fieldInstances
						const builtFieldTemplates = await Promise.all(fieldTemplatePromises);
						const fieldInstanceObj = await buildFieldInstances(builtFieldTemplates, formInstance, formGroup);

						if (fieldInstanceObj.linked) {
							// @ts-ignore
							formGroup.linked = fieldInstanceObj.linked;
						}

						const fieldInstances = fieldInstanceObj.instances;
						allFieldInstances = allFieldInstances.concat(fieldInstances);
						allImageDocs = allImageDocs.concat(fieldInstanceObj.imageDocs);

						const fullForm: FormInstance = {
							...formInstance,
							formTemplate: formTemplate,
							fieldInstances: { items: fieldInstanceObj.fullInstances },
						};

						allFullForms.push(fullForm);
					}
				}

				// Batch operations - using offline equivalents
				await saveFormGroup(formGroupForResolver);
				await bulkSaveFormInstances(allFormInstances);
				await bulkSaveFieldInstances(allFieldInstances);
				await bulkSaveImageDocs(allImageDocs);

				formGroup.formInstances = {
					items: allFullForms,
				};

				const newFormGroups = [...formGroups];
				newFormGroups.push(formGroup);
				setFormGroups(newFormGroups);

				// Track form group creation (offline)
				try {
					await trackUserBehaviorEvent("form_group_create", {
						formGroupId: formGroup.id,
						formGroupName: formGroup.name,
						formGroupType: formGroup.type,
						projectId: project.id,
						userId: userDoc.id,
						groupId: userDoc.group,
					});

					// Track creation of each form in the group
					for (const form of allFullForms) {
						await trackUserBehaviorEvent("form_create", {
							formId: form.id,
							formName: form.formTemplate?.templateName ?? (form.formTemplate?.template || "Unknown template"),
							formGroupId: formGroup.id,
							templateUsed: true,
							formTemplateId: form.formTemplateId || "",
							userId: userDoc.id,
							groupId: userDoc.group,
						});
					}
				} catch (error) {
					console.error("Failed to track form group/form creation events (offline):", error);
				}

				addNewTab(formGroup);
				return formGroup;
			}
		} catch (error) {
			recordSentry({ error, msg: "Error creating formGroup:", showModal: true });
		}
	}

	async function createNewInstance(
		template: FormTemplate,
		formGroup: FormGroup,
		linkedAltIds: Record<string, string>,
		linkedData: Record<string, FieldInstance>,
	): Promise<FormInstance | void> {
		try {
			if (template.fieldTemplates) {
				if (online) {
					const userSession = await fetchAuthSession();
					const groups = userSession.tokens?.accessToken.payload["cognito:groups"] as string[] | undefined;
					if (groups) {
						const group = groups.find((grp: string) => !grp.includes("_"));
						if (group) {
							const formInstanceId = uuid();
							const timestamp = new Date().toISOString();
							const formInstance: FormInstanceForResolver = {
								id: formInstanceId,
								formTemplateId: template.id,
								formGroup: formGroup.id,
								group,
								createdAt: timestamp,
								updatedAt: timestamp,
							};
							formGroup.formOrder.push(formInstanceId);
							const updatedFormGroup = {
								id: formGroup.id,
								name: formGroup.name,
								group: formGroup.group,
								formOrder: formGroup.formOrder,
								type: formGroup.type,
								users: formGroup.users,
								updatedAt: new Date().toISOString(),
								lastUpdatedBy: userDoc.username,
								syncedAt: formGroup.syncedAt,
							};
							const client = generateClient({ authMode: "userPool" });
							await client.graphql({
								query: createFormInstance,
								variables: { input: formInstance },
								authMode: "userPool",
							});
							await client.graphql({
								query: updateFormGroupGql,
								variables: { input: updatedFormGroup },
								authMode: "userPool",
							});
							const fieldInstanceObj = await buildFieldInstances(
								template.fieldTemplates.items,
								formInstance,
								formGroup,
								linkedAltIds,
								linkedData,
							);
							const fieldInstances = fieldInstanceObj.instances;

							await handleBatchCrud(fieldInstances, batchCreateFieldInstances, 25);
							await handleBatchCrud(fieldInstanceObj.imageDocs, batchCreateImages, 25);

							const fullForm: FormInstance = {
								...formInstance,
								formTemplate: template,
								fieldInstances: {
									items: fieldInstanceObj.fullInstances,
								},
							};

							// Track form creation event (online)
							try {
								await trackUserBehaviorEvent("form_create", {
									formId: formInstance.id,
									formName: template.template,
									formGroupId: formGroup.id,
									templateUsed: true,
									formTemplateId: template.id,
									userId: userDoc.id,
									groupId: userDoc.group,
								});
							} catch (error) {
								console.error("Failed to track form creation event (online):", error);
							}

							return fullForm;
						}
					}
				} else {
					// We are trying to add a formInstance into a formGroup. How do? We need to create the formInstance with all of its children, and update the parent formGroup
					// Step 1: Create a skeleton of the formInstance doc
					// Step 2: Push the new formInstance id into the formGroup's formOrder array
					// Step 3: Create the updated formGroup doc
					// Step 4: Create a promise array
					// Step 5: Create promise to update the formGroup document
					// Step 6: Create promise to create the formInstance
					// Step 7: Build the fieldInstanceObj
					// Step 8: Create promise to bulk create the fieldInstances
					// Step 9: Await all promises
					// Step 10: create formGroup with its children and return

					// Step 1: Create a skeleton of the formInstance doc
					const formInstanceId = uuid();
					const createdAt = new Date().toISOString();
					const formInstanceDoc: FormInstanceForResolver = {
						id: formInstanceId,
						formTemplateId: template.id,
						formGroup: formGroup.id,
						group: userDoc.group,
						createdAt,
						updatedAt: createdAt,
					};

					// Step 2: Push the new formInstance id into the formGroup's formOrder array
					formGroup.formOrder.push(formInstanceId);

					// Step 3: Create the updated formGroup doc
					const updatedFormGroup: FormGroupForResolver = {
						id: formGroup.id,
						name: formGroup.name,
						group: formGroup.group,
						formOrder: formGroup.formOrder,
						type: formGroup.type,
						users: formGroup.users,
						projectId: formGroup.projectId,
						createdAt: formGroup.createdAt,
						updatedAt: createdAt,
						lastUpdatedBy: userDoc.username,
						syncedAt: formGroup.syncedAt,
					};

					// Step 4: Create a promise array
					const promises = [];

					// Step 5: Create promise to update the formGroup document
					promises.push(updateFG(updatedFormGroup));

					// Step 6: Create promise to create the formInstance
					promises.push(createFI(formInstanceDoc));

					// Step 7: Build the fieldInstanceObj
					const fieldInstanceObj = await buildFieldInstances(template.fieldTemplates.items, formInstanceDoc, formGroup, linkedAltIds, linkedData);

					// Step 8: Create promise to bulk create the fieldInstances
					promises.push(bulkSaveFieldInstances(fieldInstanceObj.instances));
					// promises.push(createImageDocs(fieldInstanceObj.imageDocs));
					// Step 9: Await all promises
					await Promise.all(promises);

					// Step 10: create formGroup with its children and return
					const fullForm: FormInstance = { ...formInstanceDoc, formTemplate: template, fieldInstances: { items: fieldInstanceObj.fullInstances } };

					// Track form creation event (offline)
					try {
						await trackUserBehaviorEvent("form_create", {
							formId: formInstanceDoc.id,
							formName: template.template,
							formGroupId: formGroup.id,
							templateUsed: true,
							formTemplateId: template.id,
							userId: userDoc.id,
							groupId: userDoc.group,
						});
					} catch (error) {
						console.error("Failed to track form creation event (offline):", error);
					}

					return fullForm;
				}
			}
		} catch (error) {
			recordSentry({ error, msg: "Error creating form instance and field instances:", showModal: true });
		}
	}

	/**
	 * Creates the new field instances and orders them, for tables it puts them in a row.
	 */
	async function buildFieldInstances(
		fieldTemplates: FieldTemplate[],
		formInstance: Pick<FormInstanceForResolver, "id" | "group"> & (Partial<FormInstanceForResolver> | Partial<FormInstance>),
		formGroup: FormGroup,
		linkedAltIds?: Record<string, string>,
		linkedData?: Record<string, FieldInstance>,
	) {
		const fieldArray = [];
		const fullFieldArray = [];
		const linked: Record<string, string> = {};
		const imageDocs = [];
		// Still need to map through to build the correct order
		for (let s = 0; s < fieldTemplates.length; s++) {
			const fieldTemplate = fieldTemplates[s];
			if (fieldTemplate.tableField) {
				continue;
			}

			const id = uuid();
			let tableFields = [];
			let altFieldObj: FieldInstanceForResolver | undefined;
			// setup the field object for now just putting type text, this would be where that would be determined
			const fieldObjCreated = new Date().toISOString();
			const fieldObj: FieldInstanceForResolver = {
				id,
				formGroup: formGroup.id,
				formInstance: formInstance.id,
				fieldTemplateId: fieldTemplate.id,
				value: "",
				authors: [],
				lastAuthor: "",
				group: formGroup.group,
				createdAt: fieldObjCreated,
				updatedAt: fieldObjCreated,
				notes: "",
				editing: "",
				fieldsOrder: [],
			};
			if (!online) fieldObj.touched = 1;

			if (fieldTemplate.action && fieldTemplate.action.length > 0) {
				// action is now an array
				// only the preform type actions need to be handled differently at this point.
				// for now all we have for preform is a LINK field
				let preformActionElementData;
				let preformAction;

				for (let i = 0; i < fieldTemplate.action.length; i++) {
					const action = fieldTemplate.action[i];
					// Just checking for LINK type fields for now (until we need a new type at this point)
					if (action === "LINK") {
						preformAction = action;
						preformActionElementData = fieldTemplate.actionData[i];
					}

					if (action === "LINK_TO") {
						preformAction = action;
						preformActionElementData = `${formInstance.id}_${fieldTemplate.actionData[i]}`;
					}
					if (action === "LINK_FROM") {
						preformActionElementData = fieldTemplate.actionData[i];
						preformAction = action;
					}

					if (action === "PREPOPULATE" || action === "PREPOPULATE_PICKLIST") {
						const preData = fieldTemplate.actionData[i];
						fieldObj.value = preData;
					}

					if (action === "PREPOPULATE_CURRENT_DATE") {
						fieldObj.value = prepopulateCurrentDateAction();
					}
				}
				if (preformAction === "LINK" || preformAction === "LINK_TO") {
					const actionId = preformActionElementData;
					if (actionId) {
						if (linkedAltIds && !linkedAltIds[actionId]) {
							// If the id doesnt already exist in the linked map setup the alt doc
							if (!linked[actionId]) {
								altFieldObj = handleLinkedSetup(fieldObj);
								linked[actionId] = altFieldObj.id;
								fieldObj.altInstanceId = altFieldObj.id;
								fieldArray.push(altFieldObj);
							} else {
								// otherwise just setup the relationship
								fieldObj.altInstanceId = linked[actionId];
							}
						} else if (linkedAltIds && linkedAltIds[actionId] && linkedData) {
							// the linked already exists so just set this alt id to that
							fieldObj.altInstanceId = linkedAltIds[actionId];
							altFieldObj = linkedData[linkedAltIds[actionId]];
						} else {
							// basically fresh?
							if (actionId && !linked[actionId]) {
								altFieldObj = handleLinkedSetup(fieldObj);
								linked[actionId] = altFieldObj.id;
								fieldObj.altInstanceId = altFieldObj.id;
								fieldArray.push(altFieldObj);
							} else {
								// otherwise just setup the relationship
								fieldObj.altInstanceId = linked[actionId];
							}
						}
					}
				}
			}

			if (fieldTemplate.tableFields && fieldTemplate.tableFields.items.length > 0) {
				// an object to hold each obj to be sorted into the table after the first loop is
				// finished
				const tableFieldsMap: Record<string, string> = {};
				let newFieldsOrderArray = [];

				// the table fields should be in with the other templates
				for (let i = 0; i < fieldTemplate.tableFields.items.length; i++) {
					const tableFieldTemplate = fieldTemplate.tableFields.items[i];

					let tableFieldId = uuid();

					const tableObjCreated = new Date().toISOString();
					const tableFieldObj: FieldInstanceForResolver = {
						id: tableFieldId,
						formGroup: formGroup.id,
						fieldTemplateId: tableFieldTemplate.id,
						tableField: id,
						value: "",
						authors: [],
						lastAuthor: "",
						group: formGroup.group,
						createdAt: tableObjCreated,
						updatedAt: tableObjCreated,
						fieldsOrder: [],
						notes: "",
						editing: "",
					};

					// THIS IS NOT NEEDED WITH SINGLE GEO FIELD SETUP ANYMORE
					// if (fieldTemplate.inputType === "geospatial") {
					// 	// if its geospatial lets make sure there is a 1st row with fields order setup
					// 	newFieldsOrderArray.push(tableFieldObj.id);
					// }

					if (!online) tableFieldObj.touched = 1;

					let cellAltFieldObj: FieldInstanceForResolver | undefined;
					if (tableFieldTemplate.action && tableFieldTemplate.action.length > 0) {
						let cellPreformAction: string | undefined;
						let cellPreformActionData: string | undefined;
						for (let i = 0; i < tableFieldTemplate.action.length; i++) {
							const actionItem = tableFieldTemplate.action[i];
							// Just checking for LINK type fields for noww (until we need a new type at this point)
							const actionElementArray = actionItem.split("_");
							const action = actionElementArray[0];

							if (action === "PREPOPULATE" || action === "PREPOPULATE_PICKLIST") {
								const preData = fieldTemplate.actionData[i];
								tableFieldObj.value = preData;
							}

							if (action === "PREPOPULATE_CURRENT_DATE") {
								tableFieldObj.value = prepopulateCurrentDateAction();
							}

							if (action === "LINK") {
								cellPreformAction = action;
								cellPreformActionData = tableFieldTemplate.actionData[i];
							}

							if (action === "LINK_TO") {
								cellPreformAction = action;
								cellPreformActionData = `${formInstance.id}_${tableFieldTemplate.actionData[i]}`;
							}
						}
						if (cellPreformAction === "LINK" || cellPreformAction === "LINK_TO") {
							const actionId = cellPreformActionData;
							if (actionId) {
								if (linkedAltIds && !linkedAltIds[actionId]) {
									if (!linked[actionId]) {
										cellAltFieldObj = handleLinkedSetup(tableFieldObj);
										linked[actionId] = cellAltFieldObj.id;
										tableFieldObj.altInstanceId = cellAltFieldObj.id;
										fieldArray.push(cellAltFieldObj);
									} else {
										tableFieldObj.altInstanceId = linked[actionId];
									}
								} else if (linkedAltIds && linkedAltIds[actionId] && linkedData) {
									tableFieldObj.altInstanceId = linkedAltIds[actionId];
									cellAltFieldObj = linkedData[linkedAltIds[actionId]];
								} else {
									if (!linked[actionId]) {
										cellAltFieldObj = handleLinkedSetup(tableFieldObj);
										linked[actionId] = cellAltFieldObj.id;
										tableFieldObj.altInstanceId = cellAltFieldObj.id;
										fieldArray.push(cellAltFieldObj);
									} else {
										tableFieldObj.altInstanceId = linked[actionId];
									}
								}
							}
						}
					}

					// Add the imageId in here (the order is specific because we separate what is created
					// in the db here and then normalize before sending the objects back for state)
					if (tableFieldTemplate.inputType === "image") {
						const imageId = uuid();
						tableFieldObj.imageId = imageId;
					}

					// add it to the list to be created in the batch create
					fieldArray.push(tableFieldObj);
					// now add the template and image if needed, we need to do this after because the created doc
					// cannot have the fieldTemplate in the fieldTemplate prop
					const fullTableFieldObj: FieldInstance = { ...tableFieldObj, tableFields: { items: [] } };
					fullTableFieldObj.fieldTemplate = tableFieldTemplate;
					if (tableFieldTemplate.inputType === "image" && fullTableFieldObj.imageId) {
						const timestamp = new Date().toISOString();
						const imageDoc: Image = {
							id: fullTableFieldObj.imageId,
							formGroup: formGroup.id,
							project: formGroup.projectId,
							key: "",
							rotation: "",
							dpi: "",
							aspectX: "",
							aspectY: "",
							group: formGroup.group,
							createdAt: timestamp,
							updatedAt: timestamp,
						};
						fullTableFieldObj.image = imageDoc;
						imageDocs.push(imageDoc);
					}

					// Wire up altInstance for LINK cells
					if (cellAltFieldObj) {
						fullTableFieldObj.altInstance = cellAltFieldObj;
						fullTableFieldObj.altInstanceId = cellAltFieldObj.id;
					} else if (tableFieldObj.altInstanceId && linkedData?.[tableFieldObj.altInstanceId]) {
						fullTableFieldObj.altInstance = linkedData[tableFieldObj.altInstanceId];
					}

					// add it to the object that will be sorted and added to the table as the fields
					// Order
					tableFieldsMap[fullTableFieldObj.fieldTemplate.id] = fullTableFieldObj.id;
					tableFields.push(fullTableFieldObj);
				}
			}
			// PREVIOUS GEO SPATIAL STUFF NOT SURE IF WE NEED STILL BUT WELL NEED PREOPOPULATE AT LEAST
			// COMMENTING FOR NOW!!!
			// const stringifyGeo = (lat: number, lon: number) => JSON.stringify({ lat, lon });
			if (fieldTemplate.inputType === "geospatial" || fieldTemplate.inputType === "geospatialutm") {
				// if its geospatial lets make sure there is a 1st row with fields order setup
				// fieldObj.fieldsOrder = newFieldsOrderArray;
				// Also need to check the action on the geospatial field template to see if
				// PREPOPULATE_LOCATION action is set, then we need to grab the location data and set to its children
				const geospatialActionExists = fieldTemplate.action?.includes("PREPOPULATE_LOCATION");
				if (geospatialActionExists) {
					// grab the location data from wherever it is stored (likely from the form group or some geo service)
					// and set it to the children fields of this geospatial field instance
					try {
						// FOR DEVELOPMENT TESTING PURPOSES WILL HARD CODE THIS THEN TEST ON IPAD WITH THIS METHOD
						const locationData = await GeoLocationService.getCurrentPosition();

						// For non location allowed laptop testing -
						// const locationData = {
						// 	status: "success",
						// 	latitude: 37.7749,
						// 	longitude: -122.4194,
						// 	accuracy: 5
						// };

						if (locationData.status === "success") {
							const { latitude, longitude } = locationData;

							// Store a single JSON string
							fieldObj.value = JSON.stringify({ lat: latitude, lon: longitude });

							// Optional debug log
						} else {
							// Leave fieldObj.value empty to represent "unset"
							fieldObj.value = "";
						}
					} catch (e) {
						console.log("Exception while getting location data:", e);

						// Leave empty/unset
						fieldObj.value = "";
					}
				}
			}

			// the order of operations matters here
			// First in the fieldArray to be created (deep copy)
			fieldArray.push(fieldObj);

			// This needs to happen here because of the actions that may be setting things up need to be included in this.
			const fullFieldObjStart: FieldInstance = { ...fieldObj, tableFields: { items: [] } };

			// Add the tableFields on
			if (fieldTemplate.tableFields && fieldTemplate.tableFields.items.length > 0) {
				// again its a table, but now we add on the tableFields, so we don't try to create
				// the table with tableFields because thats not actually a prop for the instance (its a relationship)
				fullFieldObjStart.tableFields = { items: tableFields as FieldInstance[] };
			}

			if (fieldTemplate.action && altFieldObj) {
				fullFieldObjStart.altInstance = altFieldObj;
				fullFieldObjStart.altInstanceId = altFieldObj.id;
			}

			// setup the obj to return so the tab can be setup with the full instance and template
			const fullFieldObj: FieldInstance = {
				...fullFieldObjStart,
				fieldTemplate,
			};

			fullFieldArray.push(fullFieldObj);
		}

		// setup final return obj with the instances that need to be batch created and the full
		// instances that are passed back to the tab for rendering.
		const obj = {
			instances: fieldArray,
			fullInstances: fullFieldArray,
			linked,
			imageDocs,
		};
		return obj;
	}

	/**
	 * Handles creating the altInstance and setting the relationship up on the field.
	 * then returns the new instance.
	 * @param fieldObj with the meta field instance data for a form group
	 */
	function handleLinkedSetup(fieldObj: FieldInstanceForResolver): FieldInstanceForResolver {
		const id = uuid();
		const altFieldObj: FieldInstanceForResolver = {
			id,
			formGroup: fieldObj.formGroup,
			value: fieldObj.value, // this is so prepops make it in.
			authors: [],
			group: fieldObj.group,
			fieldTemplateId: null,
			fieldsOrder: null,
			notes: "",
			editing: "",
			createdAt: fieldObj.createdAt || new Date().toISOString(),
			updatedAt: fieldObj.updatedAt || new Date().toISOString(),
		};

		return altFieldObj;
	}

	/**
	 * Deletes the selected form groups and everything in it, then returns true or an error
	 * @param selected ids of form groups to be deleted.
	 */
	async function deleteSelectedFormGroups(selected: string[]) {
		try {
			const formGroupsToDelete = new Set(selected);
			const newFormGroups = formGroups.filter((formGroup) => !formGroupsToDelete.has(formGroup.id));
			setFormGroups(newFormGroups);
			const formGroupObjs = selected.map((id) => {
				return { id };
			});
			await deleteFormGroups(formGroupObjs, online);
		} catch (error) {
			recordSentry({ error, msg: "Error deleting the selected form groups:" });
		}
	}

	function removeFormGroupsFromListener(ids: string[]): void {
		const formGroupsToDelete = new Set(ids);
		const newFormGroups = formGroups.filter((formGroup) => !formGroupsToDelete.has(formGroup.id));
		setFormGroups(newFormGroups);
	}

	/**
	 * Given an array of formGroups, adds them into state. For now, THE ONLY USE FOR THIS IS TRANSFERRING A PROJECT TO ONLINE IN THE SYNC TAB
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param formGroupsToAdd The form groups we want to add into state
	 */
	function addFormGroupsInState(formGroupsToAdd: FormGroup[]): void {
		// Make copy of old state
		const finalFormGroups = [...formGroups];
		for (const formGroup of formGroupsToAdd) {
			// Check and see if old state has this formGroup
			const oldFormGroupIndex = finalFormGroups.findIndex((fg) => fg.id === formGroup.id);
			if (oldFormGroupIndex !== -1) {
				// If the original state has the formGroup, we need to splice it out with the updated one
				finalFormGroups.splice(oldFormGroupIndex, 1, formGroup);
			} else {
				// It isn't in the old state, push it on
				finalFormGroups.push(formGroup);
			}
		}
		// Set state
		setFormGroups(finalFormGroups);
	}

	async function fetchFormGroupsForState(ids: string[]): Promise<void> {
		const formGroupsToAdd = await batchGetFormGroupsToFormInstance(ids, online);
		const finalFormGroups = [...formGroups];
		for (const formGroup of formGroupsToAdd) {
			// Check and see if old state has this formGroup
			const oldFormGroupIndex = finalFormGroups.findIndex((fg) => fg.id === formGroup.id);
			if (oldFormGroupIndex !== -1) {
				// If the original state has the formGroup, we need to splice it out with the updated one
				finalFormGroups.splice(oldFormGroupIndex, 1, formGroup);
			} else {
				// It isn't in the old state, push it on
				finalFormGroups.push(formGroup);
			}
		}
		setFormGroups(finalFormGroups);
	}

	const results = {
		addFormGroupsInState,
		createNewInstance,
		createNewFormGroup,
		addFormGroup,
		getFormTemplates,
		addFormTemplateToListInState,
		removeTemplateFromListInState,
		handleClearAllTabs,
		handleRefreshTabs,
		getNewFormGroup,
		deleteSelectedFormGroups,
		removeFormGroupsFromListener,
		removeTab,
		removeMultipleTabs,
		setLoading,
		getJson,
		fetchFormGroupsForState,
		// updateFormGroup,
		loading,
		formTemplates,
		tabs,
		formGroups,
	};

	return results;
}

export default useForms;
